// GA4 + UTM helper for UniversalHouse TX
// - Captures utm_* params on load and stores in sessionStorage so they persist
// - Appends stored UTMs to outbound Airbnb URLs
// - Fires a custom `airbnb_click` event (NOT a booking / conversion)

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id"];
const STORAGE_KEY = "uhtx_utms";

function safeSessionGet() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function safeSessionSet(obj) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    /* storage disabled — ignore */
  }
}

// Read current URL's UTMs, merge into stored (new landing overrides old).
export function captureUtms() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const fresh = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) fresh[k] = v;
  });
  if (Object.keys(fresh).length) {
    safeSessionSet({ ...safeSessionGet(), ...fresh });
    return fresh;
  }
  return safeSessionGet();
}

export function getStoredUtms() {
  return safeSessionGet();
}

// Append stored UTMs onto an outbound URL, without clobbering existing params.
export function appendUtms(url) {
  try {
    const u = new URL(url);
    const utms = getStoredUtms();
    Object.entries(utms).forEach(([k, v]) => {
      if (!u.searchParams.has(k)) u.searchParams.set(k, v);
    });
    return u.toString();
  } catch {
    return url;
  }
}

// Fire the airbnb_click custom event with contextual params.
// Explicitly NOT a purchase/conversion — this is a lead-out click.
export function trackAirbnbClick({ destinationUrl, source }) {
  return trackOutboundClick("airbnb_click", { destinationUrl, source, label: "airbnb" });
}

// Fire a booking_click event for the Guesty booking URL (also NOT a purchase).
export function trackBookingClick({ destinationUrl, source }) {
  return trackOutboundClick("booking_click", { destinationUrl, source, label: "guesty" });
}

function trackOutboundClick(eventName, { destinationUrl, source, label }) {
  if (typeof window === "undefined") return;
  const utms = getStoredUtms();
  const payload = {
    event_category: "outbound",
    event_label: label,
    origin_page: window.location.pathname + window.location.search,
    origin_url: window.location.href,
    destination_url: destinationUrl,
    click_source: source || "unknown",
    ...utms,
    non_interaction: false,
  };
  try {
    if (window.gtag) {
      window.gtag("event", eventName, payload);
    }
    if (window.dataLayer) {
      window.dataLayer.push({ event: eventName, ...payload });
    }
    // Meta Pixel — mirror the same click as a named custom event
    if (typeof window.fbq === "function") {
      const fbEventName = eventName === "airbnb_click" ? "Airbnb Click" : "Booking Click";
      window.fbq("trackCustom", fbEventName, {
        content_name: label,
        click_source: source || "unknown",
        destination_url: destinationUrl,
      });
    }
  } catch {
    /* analytics failure must never break UX */
  }
}
